// DS: PO58
/**
 * Topics onboarding, with suggestions driven by the Neco backend
 *
 * The backend expects a full history of suggestions it's provided, in
 * order to generate a new batch of suggestions while doing its best
 * to exclude what's already been suggested
 */
import React from 'react';
import classnames from 'classnames';
import { WithTranslation, withTranslation } from 'react-i18next';
import { PapiTopic } from '@wix/da-papi-types';
import Button from '@wix/da-shared-react/pkg/Button';
import { Url } from '@wix/da-url';
import {
  withMobileContext,
  WithMobileContextProps,
} from '@wix/da-react-context/pkg/MobileContext';
import Grid from '@wix/da-shared-react/pkg/Grid/Grid';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import OnboardingTopicCard from '@wix/da-shared-react/pkg/OnboardingTopicCard';
import SisuPage, { SisuPageSubHeader, SisuPageContent } from '../SisuPage';
import SisuForm from '../../SignupSignin/SisuForm';
import s from './TopicsPage.scss';

export interface Props {
  referer?: string;
  authMode: string;
  /** The list of topics currently displayed */
  allTopics?: PapiTopic[];
  /** The topic IDs newly added as suggestions */
  suggestedTopicIds: number[];
  /** The history of all IDs ever displayed */
  topicIdHistory: number[][];
  /** Number of topics currently displayed, for prop comparison */
  topicCount: number;
  /** The IDs of topics currently selected, no history required */
  selectedTopicIds: number[];
  /** Number of topics currently selected, for prop comparison */
  selectedTopicCount: number;
  screenLoaded(authMode): void;
  clickContinue(authMode): void;
  topicSelect(selectionHistory, itemId): void;
  topicUnselect(selectionHistory, itemId): void;
  topicSuggestions(selectionHistory): void;
}

const MIN_CHOSEN_TOPICS = 3;
const TOPIC_SPACING = 16;
const GRID_WIDTH = parseInt(s['grid-width']) || 951;
const TOPIC_THUMB_SIZE = parseInt(s['topic-thumb-size']) || 177;

const MOBILE_TOPIC_SPACING = 8;
const MOBILE_GRID_WIDTH = parseInt(s['mobile-grid-width']) || 325;
const MOBILE_TOPIC_THUMB_SIZE = parseInt(s['mobile-topic-thumb-size']) || 103;

class TopicsPage extends React.Component<
  Props & WithTranslation & WithMobileContextProps
> {
  componentDidMount() {
    this.props.screenLoaded(this.props.authMode);
    this.props.topicSuggestions([]);
  }

  render() {
    const {
      selectedTopicIds,
      suggestedTopicIds,
      topicIdHistory,
      allTopics,
      isMobile,
      t,
    } = this.props;

    return (
      <SisuPage
        bodyClassName={s['body']}
        cardClassName={s['card']}
        logoClassName={s['outer-logo']}
        title={t('topics.page.title')}
        withHero={false}
        withLogo={!isMobile}
        isUnskippable
      >
        <SisuForm
          className={s['topics-form']}
          action="/_sisu/do/savetopics"
          method="post"
          onSubmit={e => {
            e.preventDefault();
          }}
        >
          <SisuPageContent className={s['content']}>
            <DSModal
              isOpen
              useFixedMobileHeight
              useTransparentOverlay
              shouldCloseOnOverlayClick={false}
              className={s['card']}
              contentClassName={s['content']}
              heading={t('topics.title')}
              subheading={!isMobile && t('topics.subheader')}
              footerRight={this.renderFooter()}
            >
              {isMobile && (
                <SisuPageSubHeader className={s['sub-header']}>
                  {t('topics.subheader')}
                </SisuPageSubHeader>
              )}
              {!allTopics && (
                <LoadingIndicator
                  view="daSymbol"
                  coverMode={true}
                  withOverlay={true}
                />
              )}
              {allTopics && (
                <div id="topics" className={s['topics']}>
                  <Grid
                    className={s['topics-grid']}
                    elementCount={allTopics.length}
                    spacing={isMobile ? MOBILE_TOPIC_SPACING : TOPIC_SPACING}
                    containerWidth={isMobile ? MOBILE_GRID_WIDTH : GRID_WIDTH}
                    breakpoints={
                      isMobile
                        ? [
                            {
                              maxWidth: MOBILE_GRID_WIDTH,
                              elementWidth: MOBILE_TOPIC_THUMB_SIZE,
                              elementHeight: MOBILE_TOPIC_THUMB_SIZE,
                            },
                          ]
                        : [
                            {
                              maxWidth: GRID_WIDTH,
                              elementWidth: TOPIC_THUMB_SIZE,
                              elementHeight: TOPIC_THUMB_SIZE,
                            },
                          ]
                    }
                    getElementId={String}
                    renderElement={({ width, index }) => {
                      const topic = allTopics[index];
                      return (
                        <OnboardingTopicCard
                          key={topic.name}
                          className={classnames(
                            s['topic'],
                            topicIdHistory.length > 1 &&
                              suggestedTopicIds &&
                              suggestedTopicIds.includes(topic.itemId!) &&
                              s[
                                `topic-new-${suggestedTopicIds.indexOf(
                                  topic.itemId!
                                )}`
                              ]
                          )}
                          typeId={topic.typeId}
                          itemId={topic.itemId}
                          name={topic.name}
                          onClick={this.onClickTopic.bind(this)}
                          coverDeviation={
                            topic.exampleDeviations &&
                            topic.exampleDeviations[0]
                          }
                          width={width}
                          rank={index}
                          isSelected={
                            selectedTopicIds &&
                            selectedTopicIds.includes(topic.itemId!)
                          }
                        />
                      );
                    }}
                  />
                </div>
              )}
            </DSModal>
          </SisuPageContent>
        </SisuForm>
      </SisuPage>
    );
  }

  onClickContinue = () => {
    this.props.clickContinue(this.props.authMode);
    window.location.assign(this.props.referer || Url.origin());
  };

  onClickTopic = (name: string, itemId?: number) => {
    const {
      selectedTopicIds,
      topicIdHistory: history,
      topicSelect,
      topicUnselect,
    } = this.props;

    if (!itemId) {
      return;
    }

    if (selectedTopicIds.includes(itemId)) {
      topicUnselect(history, itemId);
    } else {
      topicSelect(history, itemId);
    }
  };

  renderFooter() {
    const { selectedTopicIds, allTopics, t } = this.props;
    const buttonDisabled =
      !allTopics || selectedTopicIds.length < MIN_CHOSEN_TOPICS;
    return [
      <Button
        key="submit"
        className={s['submit-button']}
        onClick={this.onClickContinue}
        type="submit"
        disabled={buttonDisabled}
      >
        {t(
          buttonDisabled
            ? 'topics.form.continue.disabled'
            : 'topics.form.continue'
        )}
      </Button>,
    ];
  }
}

export default withMobileContext(withTranslation()(TopicsPage));
